import { useEffect, useRef, useState } from "react";

import { useQuieto } from "@/hooks/useQuieto";

/**
 * HOJA ARRUGADA — un papel estrujado que se desarruga y deja ver lo que tapaba.
 *
 * DE DÓNDE SALE. Gabri, 20 de octubre de 2026: *«he pensado en hacer un text-reveal o algo
 * así: que a mano derecha haya un botón que ponga revelar y salga el ejemplo […] es para
 * darle un poco de vida y acción a la landing»*, con un componente de hoja arrugada de Framer
 * como referencia. La primera versión era una tarjeta blanca con cuatro degradados y un
 * botón, y él la vio y dijo lo que era: *«visualmente no es atractivo. le falta algo a la
 * dinámica del reveal. por eso te dije lo del papel arrugado»*. Tenía razón: no había papel.
 *
 * NO SE IMPORTA EL DE FRAMER. Es un módulo alojado en `framer.com`, y esta portada no carga
 * código de terceros en la primera pantalla: el presupuesto es de 260 kB y lo que entra por
 * ahí no se mide, no se versiona y no se puede probar sin red.
 *
 * EL PAPEL SON SESENTA FACETAS CON SU LUZ, y el sombreado no está elegido a ojo: se calcula
 * la normal de cada triángulo a partir de la altura de sus vértices y se proyecta contra una
 * luz que viene de arriba a la izquierda. Eso es lo que hace que se lea como un volumen y no
 * como una textura. Lo genera —y lo vigila— `tools/prueba_hoja_arrugada.py`, con semilla
 * fija: la malla no se escribe ni se corrige aquí, y las razones están allí.
 *
 * EL DESARRUGADO SON DOS COSAS A LA VEZ, y por eso se nota. La hoja se estira y se va
 * mientras el contenido, que ya está puesto debajo, entra. En dos tiempos —quitar y luego
 * poner— habría un parpadeo en blanco en medio, que es exactamente lo que no parece papel.
 *
 * LO QUE HAY DEBAJO NO ESTÁ EN LA PÁGINA HASTA QUE SE PIDE: un contenido tapado con CSS se lo
 * lee igual un lector de pantalla, y entonces quien no ve recibe el final antes que la
 * pregunta. Y se abre con el teclado, porque es un `<button>` y no un `div` que escucha
 * clics: quien no usa ratón también tiene derecho a ver el ejemplo.
 *
 * Y OBEDECE AL AJUSTE DE MOVIMIENTO. Un papel desarrugándose es justo lo que marea a quien
 * pidió que las cosas se estén quietas, así que ahí no hay animación: se pulsa y está el
 * contenido. Lo que se pidió fue menos movimiento, no menos contenido.
 *
 * Y LA PREGUNTA SE HACE DONDE SE HACE. La primera versión de esto se preguntaba
 * `prefers-reduced-motion` por su cuenta, y `tools/check_movimiento.py` lo paró: la única
 * llamada a `matchMedia` de la casa vive en `lib/movimiento.mjs`, porque el ajuste del alumno
 * tiene tres opciones y «completo» significa *ignora al sistema*. Preguntándole solo al
 * navegador, quien elige «completo» con el sistema en «reducir» no vería desarrugarse nada:
 * la opción no haría nada, y precisamente para quien la eligió a propósito.
 */

/** Lo que dura el desarrugado. Tiene que ser lo mismo que `hoja-se-va` en `index.css`: si
 *  aquí fuera menos, la hoja desaparecería de golpe a mitad de su propia animación.
 *
 *  Y SE EXPORTA porque no es solo suya: cuando se abre esta hoja, la tarjeta de al lado se
 *  va y ésta se centra, y ese viaje tiene que durar lo mismo. Dos cifras iguales escritas en
 *  dos ficheros se separan; una exportada, no. */
export const DURA = 680;

function Pliegues() {
  return (
    <svg className="hoja-pliegues" viewBox="0 0 100 100" preserveAspectRatio="none"
         aria-hidden="true" focusable="false">
      {/* ⟨facetas⟩ · las genera tools/prueba_hoja_arrugada.py — no se tocan a mano */}
        <polygon points="0.0,0.0 17.0,0.0 14.1,17.0" fill="#fff" opacity="0.025" />
        <polygon points="0.0,0.0 14.1,17.0 0.0,18.9" fill="#fff" opacity="0.195" />
        <polygon points="17.0,0.0 34.5,0.0 14.1,17.0" fill="#0B0B0B" opacity="0.065" />
        <polygon points="34.5,0.0 37.0,16.3 14.1,17.0" fill="#fff" opacity="0.442" />
        <polygon points="34.5,0.0 51.3,0.0 53.8,15.5" fill="#0B0B0B" opacity="0.024" />
        <polygon points="34.5,0.0 53.8,15.5 37.0,16.3" fill="#fff" opacity="0.316" />
        <polygon points="51.3,0.0 62.7,0.0 53.8,15.5" fill="#0B0B0B" opacity="0.077" />
        <polygon points="62.7,0.0 69.3,18.9 53.8,15.5" fill="#fff" opacity="0.525" />
        <polygon points="62.7,0.0 81.4,0.0 80.6,23.6" fill="#fff" opacity="0.254" />
        <polygon points="62.7,0.0 80.6,23.6 69.3,18.9" fill="#fff" opacity="0.153" />
        <polygon points="81.4,0.0 100.0,0.0 80.6,23.6" fill="#0B0B0B" opacity="0.069" />
        <polygon points="100.0,0.0 100.0,23.5 80.6,23.6" fill="#fff" opacity="0.628" />
        <polygon points="0.0,18.9 14.1,17.0 0.0,44.9" fill="#0B0B0B" opacity="0.008" />
        <polygon points="14.1,17.0 16.8,45.1 0.0,44.9" fill="#fff" opacity="0.491" />
        <polygon points="14.1,17.0 37.0,16.3 38.4,36.1" fill="#fff" opacity="0.327" />
        <polygon points="14.1,17.0 38.4,36.1 16.8,45.1" fill="#fff" opacity="0.45" />
        <polygon points="37.0,16.3 53.8,15.5 38.4,36.1" fill="#fff" opacity="0.176" />
        <polygon points="53.8,15.5 54.9,39.5 38.4,36.1" fill="#0B0B0B" opacity="0.016" />
        <polygon points="53.8,15.5 69.3,18.9 61.8,38.7" fill="#fff" opacity="0.075" />
        <polygon points="53.8,15.5 61.8,38.7 54.9,39.5" fill="#0B0B0B" opacity="0.002" />
        <polygon points="69.3,18.9 80.6,23.6 61.8,38.7" fill="#0B0B0B" opacity="0.079" />
        <polygon points="80.6,23.6 87.9,36.1 61.8,38.7" fill="#0B0B0B" opacity="0.057" />
        <polygon points="80.6,23.6 100.0,23.5 100.0,38.5" fill="#0B0B0B" opacity="0.003" />
        <polygon points="80.6,23.6 100.0,38.5 87.9,36.1" fill="#0B0B0B" opacity="0.046" />
        <polygon points="0.0,44.9 16.8,45.1 16.7,56.6" fill="#0B0B0B" opacity="0.049" />
        <polygon points="0.0,44.9 16.7,56.6 0.0,60.9" fill="#fff" opacity="0.024" />
        <polygon points="16.8,45.1 38.4,36.1 16.7,56.6" fill="#0B0B0B" opacity="0.077" />
        <polygon points="38.4,36.1 31.1,58.7 16.7,56.6" fill="#fff" opacity="0.682" />
        <polygon points="38.4,36.1 54.9,39.5 48.9,65.3" fill="#fff" opacity="0.021" />
        <polygon points="38.4,36.1 48.9,65.3 31.1,58.7" fill="#0B0B0B" opacity="0.026" />
        <polygon points="54.9,39.5 61.8,38.7 48.9,65.3" fill="#fff" opacity="0.229" />
        <polygon points="61.8,38.7 71.0,64.6 48.9,65.3" fill="#fff" opacity="0.662" />
        <polygon points="61.8,38.7 87.9,36.1 81.5,60.8" fill="#fff" opacity="0.68" />
        <polygon points="61.8,38.7 81.5,60.8 71.0,64.6" fill="#fff" opacity="0.478" />
        <polygon points="87.9,36.1 100.0,38.5 81.5,60.8" fill="#fff" opacity="0.711" />
        <polygon points="100.0,38.5 100.0,64.5 81.5,60.8" fill="#fff" opacity="0.696" />
        <polygon points="0.0,60.9 16.7,56.6 0.0,81.2" fill="#0B0B0B" opacity="0.047" />
        <polygon points="16.7,56.6 22.0,81.8 0.0,81.2" fill="#fff" opacity="0.72" />
        <polygon points="16.7,56.6 31.1,58.7 29.1,82.0" fill="#fff" opacity="0.25" />
        <polygon points="16.7,56.6 29.1,82.0 22.0,81.8" fill="#fff" opacity="0.048" />
        <polygon points="31.1,58.7 48.9,65.3 29.1,82.0" fill="#0B0B0B" opacity="0.075" />
        <polygon points="48.9,65.3 52.7,77.7 29.1,82.0" fill="#fff" opacity="0.706" />
        <polygon points="48.9,65.3 71.0,64.6 65.2,83.9" fill="#fff" opacity="0.312" />
        <polygon points="48.9,65.3 65.2,83.9 52.7,77.7" fill="#fff" opacity="0.52" />
        <polygon points="71.0,64.6 81.5,60.8 65.2,83.9" fill="#0B0B0B" opacity="0.092" />
        <polygon points="81.5,60.8 81.6,78.3 65.2,83.9" fill="#fff" opacity="0.648" />
        <polygon points="81.5,60.8 100.0,64.5 100.0,75.9" fill="#0B0B0B" opacity="0.06" />
        <polygon points="81.5,60.8 100.0,75.9 81.6,78.3" fill="#0B0B0B" opacity="0.004" />
        <polygon points="0.0,81.2 22.0,81.8 13.0,100.0" fill="#fff" opacity="0.223" />
        <polygon points="0.0,81.2 13.0,100.0 0.0,100.0" fill="#fff" opacity="0.427" />
        <polygon points="22.0,81.8 29.1,82.0 13.0,100.0" fill="#0B0B0B" opacity="0.11" />
        <polygon points="29.1,82.0 29.0,100.0 13.0,100.0" fill="#0B0B0B" opacity="0.028" />
        <polygon points="29.1,82.0 52.7,77.7 52.1,100.0" fill="#fff" opacity="0.542" />
        <polygon points="29.1,82.0 52.1,100.0 29.0,100.0" fill="#fff" opacity="0.183" />
        <polygon points="52.7,77.7 65.2,83.9 52.1,100.0" fill="#fff" opacity="0.501" />
        <polygon points="65.2,83.9 61.3,100.0 52.1,100.0" fill="#fff" opacity="0.552" />
        <polygon points="65.2,83.9 81.6,78.3 81.0,100.0" fill="#fff" opacity="0.163" />
        <polygon points="65.2,83.9 81.0,100.0 61.3,100.0" fill="#fff" opacity="0.369" />
        <polygon points="81.6,78.3 100.0,75.9 81.0,100.0" fill="#0B0B0B" opacity="0.064" />
        <polygon points="100.0,75.9 100.0,100.0 81.0,100.0" fill="#0B0B0B" opacity="0.035" />
      {/* ⟨/facetas⟩ */}
    </svg>
  );
}

export default function HojaArrugada({
  etiqueta = "Revelar", pista, children, testid, onAbrir, className = "",
}) {
  const [estado, setEstado] = useState("cerrada");   // cerrada · abriendo · abierta
  const quieto = useQuieto();
  const reloj = useRef(null);

  useEffect(() => () => clearTimeout(reloj.current), []);

  const abrir = () => {
    // AVISA ANTES DE EMPEZAR, y en la misma pulsacion: quien la monta mueve su propia
    // pantalla al mismo tiempo -la tarjeta de al lado se va, esta se centra-, y los dos
    // movimientos tienen que arrancar en el mismo fotograma o se ven como dos cosas.
    if (onAbrir) onAbrir(quieto);
    if (quieto) { setEstado("abierta"); return; }
    setEstado("abriendo");
    reloj.current = setTimeout(() => setEstado("abierta"), DURA);
  };

  // UNA SOLA RAIZ, Y ESTO COSTO UNA VUELTA ENTERA. Antes habia dos `return`: un `<button>`
  // cuando estaba cerrada y un `<div>` cuando no. Son DOS ELEMENTOS DISTINTOS, asi que al
  // abrirse React quita uno y monta otro —y un elemento recien montado no transiciona: nace
  // ya con el valor final—. Quien la coloca anima su `transform` para llevarla al centro, y
  // ese viaje no ocurria nunca: la tarjeta aparecia centrada de golpe. Gabri lo vio dos veces
  // seguidas, la segunda con razon de sobra: *«esta exactamente igual de mal que antes»*.
  //
  // LA REGLA: **lo que se anima tiene que ser el mismo elemento antes y despues.** Lo que
  // cambia es lo de dentro; la caja se queda.
  //
  // Y LA CAJA LA DIBUJA ESTA PIEZA, NO QUIEN LA LLENA. Esa fue la segunda mitad del mismo
  // fallo: cerrada era el papel —`p-8`, color crema, su sombra— y abierta una tarjeta blanca
  // que ponia `LaRuta` desde fuera con otro relleno. Dos tarjetas distintas en el mismo
  // hueco, y se notaba justo donde Gabri miro: *«el tamaño no es igual. fijate en los
  // bordes»*. Ahora el borde, el radio y el alto son de aqui y son los mismos en los dos
  // estados; lo unico que cambia es la superficie de dentro.
  //
  // Y LA RAIZ NO LLEVA `h-full`, QUE ES LO QUE LA DEJABA ENANA. Esta caja es un hijo de un
  // flex cuyo contenedor no tiene alto definido, y ahi `height: 100%` no se puede resolver:
  // **resuelve a `auto`, y al hacerlo cancela el `align-items: stretch`**. O sea que pedir
  // «todo el alto» era justo lo que le impedia tenerlo, y la hoja se quedaba en el alto de
  // su contenido —un pildora y dos renglones— mientras la tarjeta de al lado medía el doble.
  // El alto se lo da el estirado del flex, que es gratis; el `h-full` va DENTRO, donde el
  // padre ya tiene un alto definitivo del que tomar el cien por cien.
  //
  // Y LO DE DENTRO NO PUEDE EMPUJAR LA CAJA. En pantalla ancha el cuerpo va posicionado
  // sobre el hueco —`.hoja-cuerpo`, en `index.css`—, asi que el alto de la fila lo decide
  // SIEMPRE la tarjeta de al lado y nunca lo que se revela. Sin eso, el contenido abierto es
  // mas alto que el papel cerrado y la tarjeta crece al destaparse: *«cuando se muestra solo
  // la de la derecha se hace mas grande»*. En movil no se posiciona —no hay nada al lado de
  // lo que tomar el alto— y el cuerpo manda, que ahi es lo correcto.
  return (
    <div className={`${className} relative`}
         data-testid={estado === "cerrada" ? undefined : testid}>
      {/* EL SUELO ES PARA EL MOVIL. Ahi las dos tarjetas van una debajo de otra, asi que no
          hay estirado del que tomar el alto y la caja mide lo que mida su contenido: cerrada
          serian una pildora y dos renglones, o sea una tira. En pantalla ancha no estorba,
          porque el estirado da mas. */}
      <div className="rounded-ficha overflow-hidden h-full min-h-[220px] relative">
        {estado === "cerrada" ? (
          <button
            type="button"
            onClick={abrir}
            aria-expanded={false}
            data-testid={testid ? `${testid}-cerrada` : undefined}
            className="hoja-arrugada group w-full h-full flex flex-col items-center
                       justify-center gap-3 p-8 text-center focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-alpha-black"
          >
            <Pliegues />
            <span className="relative font-mono-alpha text-[10px] uppercase tracking-[0.2em]
                             bg-white/85 border border-alpha-black/15 rounded-full px-6 py-3
                             shadow-[0_8px_22px_-12px_rgba(11,11,11,0.55)]
                             group-hover:border-alpha-red group-hover:text-alpha-red transition-colors">
              {etiqueta}
            </span>
            {pista && (
              <span className="relative text-[12.5px] text-alpha-black/50 leading-snug">
                {pista}
              </span>
            )}
          </button>
        ) : (
          <div className="hoja-cuerpo hoja-desdoblada rounded-ficha bg-white p-5 sm:p-6 flex flex-col">
            {children}
          </div>
        )}
        {/* LA HOJA SIGUE PUESTA MIENTRAS SE VA, encima y sin estorbar al raton: es lo que
            hace que el contenido asome POR DEBAJO del papel en vez de aparecer donde habia
            un hueco. */}
        {estado === "abriendo" && (
          <div className="hoja-arrugada hoja-arrugada--fuera" aria-hidden="true">
            <Pliegues />
          </div>
        )}
      </div>
    </div>
  );
}
